import { reactive, toRefs } from '@vue/composition-api';
import { Session, SessionExercise } from '@/helpers/types';
import { useStore } from '@/utils/use-store';

export function useSession() {
  const store = useStore();
  const { accessToken: token } = store.state.user;
  const sessionTemplate = {
    name: '',
    day: '',
    icon: null,
    badge: null,
    type: 0,
    date: Date.now(),
    warmup: null,
    homework: null,
    exercise: [] as SessionExercise[],
  };
  const state = reactive({
    sessionList: [] as Session[],
    sessionDrawer: false,
    tempSession: Object.assign({}, sessionTemplate) as {[key: string]: any} | null,
    mode: 'create',
    loading: false,
    exerciseOrderLoading: false,
    setDefaultOrder: false
  });
  const { ServiceFactory } = require('@/service/ServiceFactory');
  const instance = ServiceFactory.get('session');

  /**
   * Fetch sessions from the API
   * @param {string} token
   * @param {any[]} [params=[]]
   * */
  async function fetchSessions(token: string, params = []) {
    try {
      const { data } = await instance.list(token, params);
      state.sessionList = data;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Fetch sessions from the API
   * @param {string} token
   * */
  async function fetchIntroSessions(token: string) {
    try {
      const { data } = await instance.list(token, 'intro');
      state.sessionList = data;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Fetch sessions from the API
   * @param {string} token
   * */
  async function fetchWODSessions(token: string) {
    try {
      const { data } = await instance.list(token, 'wod');
      state.sessionList = data;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Fetch session from the API
   * @param {string} token
   * */
  async function fetchDailySession(token: string) {
    try {
      const { data } = await instance.getWod(token);
      state.tempSession = data;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Handler for close drawer event
   * */
  function closeDrawerEventHandler() {
    state.sessionDrawer = false;
    state.mode = 'create';
    state.tempSession = Object.assign({}, sessionTemplate);
  }

  /**
   * Handler for toggle drawer event
   * @param {string|number} mode
   * */
  function toggleDrawerEventHandler(mode: string | number) {
    if (typeof mode === 'number') {
      state.tempSession = Object.assign({}, state.sessionList.find(session => session.id === mode)) as any;
    } else {
      state.tempSession = Object.assign({}, sessionTemplate);
    }
    state.mode = typeof mode === 'number' ? 'edit' : mode;
    state.sessionDrawer = true;
  }

  /**
   * Handler for create session event
   * @param {any} payload
   * */
  async function createSessionEventHandler(payload: any) {
    state.loading = true;
    try {
      await instance.create(token, payload);
      await fetchSessions(token);
      closeDrawerEventHandler();
    } catch (e) {
      throw e;
    } finally {
      state.loading = false;
    }
  }

  /**
   * Handler for edit session event
   * @param {any} payload
   * */
  async function editSessionEventHandler(payload: any) {
    state.loading = true;
    const session: Session = state.tempSession as unknown as Session;
    try {
      await instance.update(token, session.id, payload);
      await fetchSessions(token);
      closeDrawerEventHandler();
    } catch (e) {
      throw e;
    } finally {
      state.loading = false;
    }
  }

  async function changeExerciseOrderHandler(payload: any) {
    state.exerciseOrderLoading = true;
    const session: Session = state.tempSession as unknown as Session;
    try {
      await instance.changeExerciseOrder(token, session.id, payload);
      await fetchSessions(token);
    } catch (e) {
      state.setDefaultOrder = true;
      throw e;
    } finally {
      state.exerciseOrderLoading = false;
    }
  }

  return {
    ...toRefs(state),
    fetchSessions,
    fetchDailySession,
    closeDrawerEventHandler,
    toggleDrawerEventHandler,
    createSessionEventHandler,
    editSessionEventHandler,
    changeExerciseOrderHandler,
  };
}
